import { useEffect } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import useGetPatients from "../patients/useGetPatients";
import { useFetchSubmissions } from "utils/hooks";
import { AssignmentSubmission } from "types";

type Props = {
  patientSlug?: string;
  groupId?: string;
  slug?: string;
  id?: string;
  email?: string;
  currentPatient?: Array<any>;
};

const useGetSubmissions = ({
  patientSlug,
  groupId,
  slug,
  id,
  email,
  currentPatient,
}: Props) => {
  const { fetchGroupSubmissions } = useFetchSubmissions();
  const submissions = useSelector(
    (state: RootStateOrAny) => state.submissions && state.submissions
  );

  useEffect(() => {
    fetchGroupSubmissions(groupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const { patient } = useGetPatients({ email: email, slug: patientSlug });

  if (!submissions) {
    return {
      submissions: null,
      submission: null,
      patientSubmissions: null,
      groupSubmissions: null,
    };
  }

  const formatSubmission = (submission: any) => {
    if (!submission) {
      return null;
    }

    // Get valid section IDs from the assignment if available
    const validSectionIds =
      submission.assignment?.sections?.map((s: any) => s.uuid) || [];

    // First, deduplicate responseFields based on fieldId within each section
    const uniqueResponseFields = submission.responseFields.reduce(
      (acc: any[], cur: any) => {
        // Only include fields from valid sections
        if (
          !validSectionIds.length ||
          validSectionIds.includes(cur.sectionId)
        ) {
          const existingField = acc.find(
            (field) =>
              field.sectionId === cur.sectionId && field.fieldId === cur.fieldId
          );
          if (!existingField) {
            acc.push(cur);
          }
        }
        return acc;
      },
      []
    );

    // Then create sections with the deduplicated fields
    const formattedSections = uniqueResponseFields.reduce(
      (acc: any, cur: any) => {
        // Only create a new section if it doesn't exist and is valid
        if (
          !acc[cur.sectionId] &&
          (!validSectionIds.length || validSectionIds.includes(cur.sectionId))
        ) {
          acc[cur.sectionId] = {
            uuid: cur.sectionId,
            sectionLabel: cur.sectionLabel,
            sectionId: cur.sectionId,
            sectionOrder: cur.sectionOrder,
            responseFields: [],
          };
        }
        // Only add the field if it's not already in the section's responseFields
        const section = acc[cur.sectionId];
        if (
          section &&
          !section.responseFields.some(
            (field: any) => field.fieldId === cur.fieldId
          )
        ) {
          section.responseFields.push(cur);
        }
        return acc;
      },
      {}
    );

    // Sort sections by sectionOrder and ensure unique sections
    const sortedSections = Object.values(formattedSections)
      .sort((a: any, b: any) => a.sectionOrder - b.sectionOrder)
      .filter(
        (section: any, index: number, self: any[]) =>
          index ===
          self.findIndex((s: any) => s.sectionId === section.sectionId)
      );

    return {
      ...submission,
      sections: sortedSections,
      isFlagged: submission.isNegative,
    };
  };

  const submission = submissions?.submissions?.find(
    (submission: any) =>
      (submission?.slug === slug && slug) || (submission?.uuid === id && id)
  );

  const groupSubmission = submissions?.groupSubmissions?.find(
    (submission: any) =>
      (submission?.slug === slug && slug) || (submission?.uuid === id && id)
  );

  const filterByPatient = currentPatient || patient;

  const patientSubmissions = submissions?.submissions?.filter(
    (submission: any) =>
      submission.userId === filterByPatient?.uuid &&
      submission.assignmentLabel !== "Safety Plan"
  );

  const practiceSubmission = submissions?.practiceSubmissions?.find(
    (submission: AssignmentSubmission) => submission.slug === slug
  );

  return {
    submissions: submissions?.submissions,
    submission: formatSubmission(submission || groupSubmission),
    patientSubmissions: patientSubmissions,
    groupSubmissions: submissions?.groupSubmissions,
    groupSubmission: formatSubmission(groupSubmission),
    practiceSubmissions: submissions?.practiceSubmissions,
    practiceSubmission: formatSubmission(practiceSubmission),
  };
};

export default useGetSubmissions;
